import { Hourglass } from 'lucide-react';
import { type ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { type TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import {
  ErrorCode,
  MAX_DECLARED_START_DAYS,
  PlanField,
  completionTooSoon,
  declaredStartProblem,
} from '@apm/contracts';
import { Button } from '../../components/ui/button';
import { DateTimePicker } from '../../components/ui/date-time-picker';
import { displayDateTime } from '../../lib/date';
import { ApiRequestError } from '../../lib/http';
import { useProgressCommitGuard } from '../git/use-progress-commit-guard';
import { useTimerStopConfirm } from '../timesheet/use-timer-stop-confirm';
import { PlanGateDialog, type PlanGateTask } from './components/plan-gate-dialog';

/** Đủ để biết việc này được hoàn thành chưa — các trường có sẵn trên `TaskView`. */
export interface CompletableTask {
  readonly id: string;
  readonly title: string;
  /**
   * Dự án của việc — có thì hỏi commit khi hoàn thành (APM-PCOM-c, nếu dự án bật).
   * `TaskView` mang sẵn trường này.
   */
  readonly projectId?: string | undefined;
  readonly completableAt?: string | null | undefined;
  readonly actualStartAt?: string | null | undefined;
  readonly createdAt?: string | undefined;
  /** Còn thiếu hạn / ước lượng để vào việc — APM-EREF-a (`TaskView.planMissing`). */
  readonly planMissing?: readonly PlanField[] | undefined;
  /** Đặt được hạn không (`TaskView.canSchedule`) — popup dùng để mở ô hạn. */
  readonly canSchedule?: boolean | undefined;
}

interface PlanPending {
  readonly task: PlanGateTask;
  readonly run: () => void;
  readonly onCancel?: (() => void) | undefined;
}

interface Pending {
  readonly title: string;
  readonly startedAt: Date;
  readonly minMinutes: number;
  readonly run: (actualStartAt: string) => void;
  readonly onCancel?: (() => void) | undefined;
}

/**
 * Hoàn thành một việc qua **hai** lời hỏi trước — APM-TMIN-b.
 *
 * 1. Thời gian tối thiểu của dự án (APM-TMIN-a): việc chưa đủ lâu kể từ lúc bắt
 *    đầu thì hiện hộp "chưa thể hoàn thành", kèm lựa chọn **khai lại giờ bắt
 *    đầu thực tế** — người ta hay quên chuyển sang "đang làm" lúc bắt đầu. Khai
 *    xong thì `run(actualStartAt)` gửi mốc ấy **cùng** lần chuyển.
 * 2. Đồng hồ bấm giờ đang chạy trên việc này (`useTimerStopConfirm`).
 *
 * Sau cả hai: dự án bật "gắn cập nhật tiến độ với commit" (APM-PCOM-c) thì hộp
 * chọn nhánh → commit mở **cuối cùng**, ngay trước thao tác; thao tác chạy rồi bản
 * báo mới gửi (`useProgressCommitGuard`). `run` trả Promise (`mutateAsync`) thì bản
 * báo chờ nó thành công.
 *
 * Trước cả hai: việc còn thiếu hạn / ước lượng giờ (APM-EREF-c) thì mở popup
 * điền nhanh — áp cho mọi lần **vào việc** (`enters`, mặc định = `completes`;
 * board truyền thêm cho đường sang "đang làm"). Lỗi `TASK_PLAN_MISSING` từ server
 * mở cùng popup, và `resend` gửi lại y nguyên thao tác.
 *
 * Biết trước bằng `task.completableAt` (server tính, cùng hàm thuần với cửa).
 * Cờ ấy có thể cũ — cài đặt vừa đổi, danh sách chưa nạp lại — nên `handleError`
 * bắt mã `TASK_COMPLETED_TOO_SOON` từ server và mở đúng hộp ấy.
 *
 * ```tsx
 * const complete = useCompleteTaskGuard();
 * complete.guard(task, toDone, (actualStartAt) => move.mutate({ ..., actualStartAt }));
 * onError: (error, vars) => complete.handleError(error, task, (at) => move.mutate({ ...vars, actualStartAt: at })) || toast.error(...)
 * {complete.dialog}
 * ```
 */
export function useCompleteTaskGuard(): {
  readonly guard: (
    task: CompletableTask,
    completes: boolean,
    run: (actualStartAt?: string) => unknown,
    onCancel?: () => void,
    enters?: boolean,
  ) => void;
  /**
   * `true` = lỗi là "chưa đủ thời gian" hoặc "thiếu hạn / ước lượng" và hộp thoại
   * đã mở; chỗ gọi đừng báo lỗi nữa. `resend` = gửi lại y nguyên (sau popup hạn /
   * ước lượng); thiếu thì popup chỉ lưu, người dùng tự kéo lại.
   */
  readonly handleError: (
    error: unknown,
    task: Pick<CompletableTask, 'title'> & Partial<Pick<CompletableTask, 'id' | 'canSchedule'>>,
    retry: (actualStartAt: string) => void,
    resend?: () => void,
  ) => boolean;
  readonly dialog: ReactNode;
} {
  const stopConfirm = useTimerStopConfirm();
  const [pending, setPending] = useState<Pending | null>(null);
  const [planPending, setPlanPending] = useState<PlanPending | null>(null);
  const timerGuard = stopConfirm.guard;
  const commits = useProgressCommitGuard();
  const askCommits = commits.ask;
  const { t } = useTranslation();

  const guard = useCallback(
    (
      task: CompletableTask,
      completes: boolean,
      run: (actualStartAt?: string) => unknown,
      onCancel?: () => void,
      enters: boolean = completes,
    ): void => {
      // Bước cuối: hộp chọn commit (APM-PCOM-c) — chỉ khi hoàn thành, dự án bật.
      const finish = (actualStartAt?: string): unknown => {
        if (!completes || !task.projectId) {
          const out = run(actualStartAt);
          // `mutateAsync` hỏng thì nơi gọi đã báo lỗi ở `onError` — đừng để thành unhandled rejection.
          if (out instanceof Promise) out.catch(() => undefined);
          return out;
        }
        askCommits(
          {
            projectId: task.projectId,
            taskId: task.id,
            trigger: 'completed',
            heading: t('progressCommits.headingCompleted', { title: task.title }),
          },
          () => run(actualStartAt),
          onCancel,
        );
        return undefined;
      };
      const missing = task.planMissing ?? [];
      if (enters && missing.length > 0) {
        setPlanPending({
          task: { id: task.id, title: task.title, missing, canSchedule: task.canSchedule },
          // Điền xong vẫn phải qua hai lời hỏi sau — nên gọi lại chính `guard` với việc đã đủ.
          run: () =>
            guardRef.current({ ...task, planMissing: [] }, completes, run, onCancel, enters),
          onCancel,
        });
        return;
      }
      if (completes && isTooSoonToComplete(task)) {
        const earliest = Date.parse(task.completableAt ?? '');
        const startIso = task.actualStartAt ?? task.createdAt;
        const startedAt = startIso ? new Date(startIso) : new Date();
        setPending({
          title: task.title,
          startedAt,
          minMinutes: Math.round((earliest - startedAt.getTime()) / 60_000),
          // Khai giờ xong vẫn phải qua lời hỏi bấm giờ.
          run: (actualStartAt) => timerGuard(task.id, true, () => finish(actualStartAt), onCancel),
          onCancel,
        });
        return;
      }
      timerGuard(task.id, completes, () => finish(), onCancel);
    },
    [timerGuard, askCommits, t],
  );
  const guardRef = useRef(guard);
  guardRef.current = guard;

  const handleError = useCallback(
    (
      error: unknown,
      task: Pick<CompletableTask, 'title'> & Partial<Pick<CompletableTask, 'id' | 'canSchedule'>>,
      retry: (actualStartAt: string) => void,
      resend?: () => void,
    ): boolean => {
      if (!(error instanceof ApiRequestError)) return false;
      if (error.code === ErrorCode.TASK_PLAN_MISSING) {
        const missing = PlanField.array().safeParse(error.details?.['missing']).data ?? [];
        if (!task.id || missing.length === 0) return false;
        setPlanPending({
          task: { id: task.id, title: task.title, missing, canSchedule: task.canSchedule },
          run: resend ?? (() => undefined),
        });
        return true;
      }
      if (error.code !== ErrorCode.TASK_COMPLETED_TOO_SOON) return false;
      const startedAt = new Date(String(error.details?.['startedAt'] ?? ''));
      const minMinutes = Number(error.details?.['minMinutes']);
      if (Number.isNaN(startedAt.getTime()) || !(minMinutes > 0)) return false;
      setPending({ title: task.title, startedAt, minMinutes, run: retry });
      return true;
    },
    [],
  );

  const dialog = (
    <>
      {planPending ? (
        <PlanGateDialog
          task={planPending.task}
          onDone={() => {
            setPlanPending(null);
            planPending.run();
          }}
          onCancel={() => {
            setPlanPending(null);
            planPending.onCancel?.();
          }}
        />
      ) : null}
      {pending ? (
        <MinDurationDialog
          pending={pending}
          onDeclare={(actualStartAt) => {
            setPending(null);
            pending.run(actualStartAt);
          }}
          onCancel={() => {
            setPending(null);
            pending.onCancel?.();
          }}
        />
      ) : null}
      {stopConfirm.dialog}
      {commits.dialog}
    </>
  );

  return { guard, handleError, dialog };
}

/** Việc này còn chưa đủ thời gian tối thiểu để hoàn thành lúc này không — theo `completableAt`. */
export function isTooSoonToComplete(task: Pick<CompletableTask, 'completableAt'>): boolean {
  return task.completableAt ? Date.parse(task.completableAt) > Date.now() : false;
}

/** `90` → "1 giờ 30 phút". */
export function formatMinutes(minutes: number, t: TFunction): string {
  const days = Math.floor(minutes / 1440);
  const hours = Math.floor((minutes % 1440) / 60);
  const mins = minutes % 60;
  const parts = [
    days > 0 ? t('taskMinDuration.unit.days', { count: days }) : null,
    hours > 0 ? t('taskMinDuration.unit.hours', { count: hours }) : null,
    mins > 0 ? t('taskMinDuration.unit.minutes', { count: mins }) : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' ') : t('taskMinDuration.unit.minutes', { count: 0 });
}

function MinDurationDialog({
  pending,
  onDeclare,
  onCancel,
}: {
  readonly pending: Pending;
  readonly onDeclare: (actualStartAt: string) => void;
  readonly onCancel: () => void;
}): ReactNode {
  const { t } = useTranslation();
  const [declared, setDeclared] = useState(pending.startedAt.toISOString());

  // Esc = Để sau, ở pha capture — cùng lý do với hộp xác nhận dừng bấm giờ.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return;
      event.stopPropagation();
      onCancel();
    };
    window.addEventListener('keydown', onKey, { capture: true });
    return () => window.removeEventListener('keydown', onKey, { capture: true });
  }, [onCancel]);

  const now = new Date();
  const earliestAt = new Date(pending.startedAt.getTime() + pending.minMinutes * 60_000);
  const at = new Date(declared);
  const problem = declaredStartProblem(at, now);
  const stillTooSoon =
    problem === null
      ? completionTooSoon(
          {
            minMinutes: pending.minMinutes,
            kind: 'task',
            actualStartAt: at,
            createdAt: at,
          },
          now,
        )
      : null;
  const hint =
    problem === 'future'
      ? t('taskMinDuration.dialog.future')
      : problem === 'too_old'
        ? t('taskMinDuration.dialog.tooOld', { days: MAX_DECLARED_START_DAYS })
        : stillTooSoon
          ? t('taskMinDuration.dialog.stillTooSoon', {
              earliest: displayDateTime(stillTooSoon.earliestAt),
            })
          : null;

  return createPortal(
    <div className="relative z-[60]">
      <div className="fixed inset-0 popup-scrim" onClick={onCancel} aria-hidden />
      <div
        role="alertdialog"
        aria-modal="true"
        aria-labelledby="min-duration-title"
        aria-describedby="min-duration-body"
        className="fixed left-1/2 top-1/2 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2 flex-col gap-3 rounded-DEFAULT border border-border bg-background p-4 shadow-xl glass-popup-sm on-scrim"
      >
        <div className="flex items-start gap-2">
          <Hourglass aria-hidden className="mt-0.5 size-4 shrink-0 text-rag-amber" />
          <h2 id="min-duration-title" className="text-base font-semibold">
            {t('taskMinDuration.dialog.title')}
          </h2>
        </div>
        <p id="min-duration-body" className="text-sm">
          {t('taskMinDuration.dialog.body', {
            title: pending.title,
            min: formatMinutes(pending.minMinutes, t),
            start: displayDateTime(pending.startedAt),
            earliest: displayDateTime(earliestAt),
          })}
        </p>

        <div className="flex flex-col gap-1.5 rounded-DEFAULT border border-border p-3">
          <p className="text-sm font-medium">{t('taskMinDuration.dialog.declareLabel')}</p>
          <p className="text-xs text-muted-foreground">{t('taskMinDuration.dialog.declareHelp')}</p>
          <DateTimePicker
            value={declared}
            onChange={setDeclared}
            aria-label={t('taskMinDuration.dialog.declareLabel')}
          />
          {hint ? (
            <p role="status" className="text-xs text-rag-amber">
              {hint}
            </p>
          ) : null}
        </div>

        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onCancel} autoFocus>
            {t('taskMinDuration.dialog.later')}
          </Button>
          <Button
            type="button"
            disabled={hint !== null}
            onClick={() => onDeclare(new Date(declared).toISOString())}
          >
            {t('taskMinDuration.dialog.declare')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
